import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';

import WorkerRow from './WorkerRow';
import { SOURCE_LABELS } from './constants';

import Button from '@/components/Button';
import { PoolNominationRow } from '@/services/preferredPoolsAPI';
import styled from '@/styles';

// ui-prototype/preferred-worker-pool/client-detail.html #nominationsList.
// Add / Reject act in one click (OQ-2: no reason capture in the reference
// design; decision_reason stays optional server-side). Review opens the
// per-source modal (plan F7).

const EmptyState = styled('div', {
  padding: '48px 0',
  textAlign: 'center',
  color: '#979797',
  fontSize: '14px',
});

const Actions = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  flexShrink: 0,
});

const ReviewLink = styled('button', {
  all: 'unset',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontSize: '14px',
  color: '#2E8722',
  cursor: 'pointer',
});

const SourceTag = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: 8,
  padding: '2px 8px',
  fontSize: '11px',
  fontWeight: 600,
  variants: {
    source: {
      client_nomination: { background: '#E7F4E6', color: '#2E8722' },
      system_suggestion: { background: '#E0EAFF', color: '#2169BA' },
    },
  },
});

// GRAVY-10393: amber marker for a suggestion whose worker dropped below
// Gold after the trigger — visible without opening Review.
const StaleTag = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: 8,
  padding: '2px 8px',
  fontSize: '11px',
  fontWeight: 600,
  background: '#FDF3E7',
  color: '#B25E09',
});

type Props = {
  nominations: PoolNominationRow[];
  skillName: string;
  decidingId: number | null;
  onReview: (nomination: PoolNominationRow) => void;
  onApprove: (nomination: PoolNominationRow) => void;
  onDecline: (nomination: PoolNominationRow) => void;
};

const NominationsTable = ({
  nominations,
  skillName,
  decidingId,
  onReview,
  onApprove,
  onDecline,
}: Props) => (
  <div data-testid="nominationsList">
    {nominations.length === 0 ? (
      <EmptyState>No pending nominations</EmptyState>
    ) : (
      nominations.map((nomination) => (
        <WorkerRow
          key={nomination.nomination_id}
          actions={
            <Actions>
              <Button
                a11yLabel={`Add ${nomination.worker.name} to pool`}
                appearance="outline"
                disabled={decidingId === nomination.nomination_id}
                iconLeft={faCheck}
                label="Add"
                status="success"
                onClick={() => onApprove(nomination)}
              />
              <Button
                a11yLabel={`Decline ${nomination.worker.name}`}
                appearance="outline"
                disabled={decidingId === nomination.nomination_id}
                iconLeft={faTimes}
                status="danger"
                onClick={() => onDecline(nomination)}
              />
            </Actions>
          }
          rookie={nomination.rookie}
          shiftsInSkillTotal={nomination.shifts_in_skill_total}
          shiftsWithClient={nomination.shifts_with_client}
          skillName={skillName}
          statsExtra={
            <>
              <SourceTag source={nomination.source}>
                {SOURCE_LABELS[nomination.source] ?? nomination.source}
              </SourceTag>
              {nomination.stale != null && (
                <StaleTag aria-label="Score dropped below Gold after this suggestion was made">
                  Stale
                </StaleTag>
              )}
              <ReviewLink type="button" onClick={() => onReview(nomination)}>
                Review
              </ReviewLink>
            </>
          }
          tier={nomination.tier}
          worker={nomination.worker}
        />
      ))
    )}
  </div>
);

export default NominationsTable;
